import React, { useState, useEffect } from "react";
import {
  Video,
  List,
  Grid,
  Play,
  FileSpreadsheet,
  Trash2,
  Loader,
  AlertCircle,
  RefreshCw,
  Calendar,
  X,
  Image as ImageIcon,
  FileText,
} from "lucide-react";
import { Pagination, ConfirmDialog } from "./design-system";
import { formatFileSizeHelper } from "../utils/helpers";
import apiClient from "../utils/apiClient";
import VideoPlayer from "./VideoPlayer";
import { useTheme } from "../utils/theme";
import { useLocation } from "react-router-dom";
import { marked } from "marked";


// Helper function to check if a file is an image based on extension
const isImage = (filename) => {
  if (!filename) return false;
  const extension = filename.split(".").pop().toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(
    extension
  );
};

// Helper function to check if a file is a PDF
const isPdf = (filename) => {
  if (!filename) return false;
  const extension = filename.split(".").pop().toLowerCase();
  return extension === "pdf";
};

const VideoDisplayPanel = ({
  videos = [],
  totalCount = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onRefresh,
  isLoading = false,
  onDeleteVideo,
  onViewCsv,
  folderId,
  folderName,
}) => {
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'card'
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sourceTypeFromUrl = searchParams.get("source_type");

  // Handle opening PDF in a new tab
  const handleOpenPdf = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const toggleRowExpansion = (videoId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(videoId)) {
      newExpandedRows.delete(videoId);
    } else {
      newExpandedRows.add(videoId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-600";
      case "processing":
        return "text-blue-600";
      case "failed":
        return "text-red-600";
      case "uploaded":
        return "text-gray-600";
      default:
        return "text-gray-600";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString();
  };

  const is_video = (video) => {
    return !isImage(video.video_name) && !isPdf(video.video_name);
  };

  return (
    <div className="pb-4">
      <div
        style={{ display: 'flex', flexDirection: 'column', height: `calc(-125px + 100vh)` }}
        className={`border rounded-lg ${"bg-gray-700 border-gray-500"
          }`}
      >
        {/* Header */}
        <div
          className={`p-4 ${"bg-gray-700 border-gray-500"
            } border-b flex justify-between items-center`}
        >
          <div>
            <h3 className="text-xl font-semibold text-blue-700">
              {folderName ? (
                <span className="capitalize">{folderName}</span>
              ) : (
                "Files"
              )}
            </h3>
            <p className="mt-1 text-sm text-blue-600">
              {totalCount || 0} files found
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div
              className={`flex p-1 rounded-lg ${"bg-gray-800"
                }`}
            >
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-md ${viewMode === "table"
                  ? "bg-gray-700 text-white"
                  : "text-gray-400"
                  }`}
                title="Table View"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode("card")}
                className={`p-2 rounded-md ${viewMode === "card"
                  ? "bg-gray-700 text-white"
                  : "text-gray-400"
                  }`}
                title="Card View"
              >
                <Grid size={16} />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg ${"bg-blue-300 hover:bg-blue-400"
                } text-blue-700 transition-colors`}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Loader size={16} className="mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="py-12 text-center">
            <Loader
              size={36}
              className={`mx-auto mb-4 ${"text-blue-400"
                } animate-spin`}
            />
            <p className={"text-gray-300"}>
              Loading files...
            </p>
          </div>
        ) : !videos?.length ? (
          <div className="py-12 text-center text-gray-500">
            <Video className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No file found</p>
            <p className="text-sm">Upload file to see them here</p>
          </div>
        ) : viewMode === "table" ? (
          // Table View
          <div className="overflow-auto">
            <table
              className={`w-full ${"bg-gray-800"}`}
            >
              <thead
                className={`${"bg-gray-700 text-gray-400"
                  }`}
              >
                <tr>
                  {sourceTypeFromUrl == "video_analytics" ? (
                    <th className="p-4 text-sm font-medium text-left">TITLE</th>
                  ) : null}
                  <th className="p-4 text-sm font-medium text-left">
                    {sourceTypeFromUrl == "video_analytics"
                      ? `VIDEO NAME`
                      : "FILE NAME"}
                  </th>
                  <th className="p-4 text-sm font-medium text-left">STATUS</th>
                  <th className="p-4 text-sm font-medium text-left">
                    RESPONSE
                  </th>
                  <th className="p-4 text-sm font-medium text-left">
                    DATE CREATED
                  </th>
                  <th className="p-4 text-sm font-medium text-left">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <React.Fragment key={video.id}>
                    <tr
                      className={`border-b ${"border-gray-900 hover:bg-gray-900"
                        } transition-colors cursor-pointer`}
                      onClick={() => {
                        if (
                          video.response &&
                          video.response !== "Analysis in progress..." &&
                          video.response !== "-"
                        ) {
                          toggleRowExpansion(video.id);
                        }
                      }}
                    >
                      {sourceTypeFromUrl == "video_analytics" ? (
                        <td className="p-4">
                          <div className="flex items-center">
                            {video.response &&
                              video.response !== "Analysis in progress..." &&
                              video.response !== "-" && (
                                <div className="mr-2">
                                  {expandedRows.has(video.id) ? (
                                    <X
                                      className={`w-4 h-4 ${"text-gray-400"
                                        }`}
                                    />
                                  ) : (
                                    <Play
                                      className={`w-4 h-4 ${"text-gray-400"
                                        }`}
                                    />
                                  )}
                                </div>
                              )}
                            <div>
                              <div
                                className={`${"text-gray-400"
                                  } font-medium capitalize truncate  whitespace-nowrap max-w-[300px]`}
                                title={video?.title || "Untitled Video"}
                              >
                                {video?.title || "Untitled Video"}
                              </div>
                            </div>
                          </div>
                        </td>
                      ) : null}
                      <td className="p-4">
                        <div className="flex items-center">
                          <div>
                            <div
                              className={`capitalize ${"text-gray-400"
                                }`}
                            >
                              {video?.video_name || "-"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div
                          className={`capitalize font-medium ${getStatusColor(
                            video?.status
                          )}`}
                        >
                          {video?.status || "-"}
                        </div>
                      </td>
                      <td className="p-4">
                        <div
                          className={`${"text-gray-400"
                            } max-w-xs truncate`}
                        >
                          {video?.response || "-"}
                        </div>
                      </td>
                      <td className="p-4">
                        <div
                          className={`${"text-gray-400"
                            }`}
                        >
                          {formatDate(video?.created_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTime(video?.created_at)}
                        </div>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex space-x-2">
                          {/* Show CSV button only if csv_id exists */}
                          {video?.csv_id && (
                            <button
                              onClick={() => onViewCsv(video)}
                              className="flex items-center px-3 py-1 text-xs font-medium text-blue-700 transition-colors bg-blue-100 rounded hover:bg-blue-200"
                            >
                              <FileSpreadsheet size={12} className="mr-1" />
                              CSV Data
                            </button>
                          )}
                          <ConfirmDialog
                            title="Delete Video"
                            description="Are you sure you want to delete this video?"
                            onConfirm={() => onDeleteVideo(video.id)}
                            okText="Yes"
                            cancelText="No"
                            destructive
                          >
                            <button className="flex items-center px-3 py-1 text-xs font-medium text-red-700 transition-colors bg-red-100 rounded hover:bg-red-200">
                              <Trash2 size={12} className="mr-1" />
                              Delete
                            </button>
                          </ConfirmDialog>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row Content */}
                    {expandedRows.has(video.id) && (
                      <tr className="border-b border-gray-200">
                        <td colSpan="6" className="p-0">
                          <div className="p-6">
                            <div className="flex flex-col gap-6 lg:flex-row">
                              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                                {/* Video Player Section */}
                                <div className="md:col-span-1">
                                  <div className="h-full p-4 border rounded-lg">
                                    <div className="mb-2 aspect-video">
                                      {isPdf(video.video_name) ? (
                                        <div className="flex flex-col items-center justify-center w-full h-full bg-gray-800">
                                          <FileText className="w-20 h-20 mb-2 text-red-500" />
                                          <button
                                            onClick={() =>
                                              handleOpenPdf(video.secure_url)
                                            }
                                            className="flex items-center px-3 py-2 text-white transition-colors bg-red-600 rounded-md hover:bg-red-700"
                                          >
                                            <FileText
                                              size={16}
                                              className="mr-2"
                                            />
                                            Open PDF
                                          </button>
                                        </div>
                                      ) : isImage(video.video_name) ? (
                                        <img
                                          src={video.secure_url}
                                          alt={video.title || "Image"}
                                          className="object-contain w-full h-full"
                                        />
                                      ) : (
                                        <VideoPlayer
                                          selectedVideo={{
                                            videoUrl: video.secure_url,
                                          }}
                                          autoPlay={true}
                                          controls={true}
                                        />
                                      )}
                                    </div>
                                    <h3 className="mt-2 font-medium">
                                      {video.title}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                      {formatDate(video.created_at)}
                                    </p>
                                  </div>
                                </div>

                                {sourceTypeFromUrl == "video_analytics" ? (
                                  <div className="md:col-span-2">
                                    <div className="h-full p-4 border rounded-lg">
                                      <h3 className="mb-3 text-xl font-semibold text-blue-700">
                                        Analysis Results
                                      </h3>

                                      <div
                                        className={`prose max-w-none ${"bg-gray-800 text-gray-400"
                                          } rounded-lg shadow-sm`}
                                      >
                                        <div className="markdown-content analysis-text overflow-auto max-h-[60vh] py-2 pl-2">
                                          {video.response ? (
                                            video.response
                                              .split("\n\n")
                                              .map((paragraph, i) => (
                                                <div
                                                  key={i}
                                                  className="my-4 leading-relaxed"
                                                  dangerouslySetInnerHTML={{ __html: marked(paragraph) }}
                                                />
                                              ))
                                          ) : (
                                            <div className="py-20 text-center text-gray-500">
                                              <Video
                                                size={40}
                                                className="mx-auto mb-4"
                                              />
                                              <p>
                                                No analysis available for this
                                                video
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className={`border rounded-lg overflow-hidden ${"bg-gray-800 border-gray-700 hover:bg-gray-700"
                    } transition-all`}
                >
                  {/* Thumbnail/Preview */}
                  <div className="relative bg-gray-900 aspect-video">
                    {video.secure_url ? (
                      isPdf(video.video_name) ? (
                        // PDF Preview
                        <>
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                            <FileText className="w-20 h-20 text-red-500" />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center transition-opacity bg-black opacity-0 bg-opacity-40 hover:opacity-100">
                            <button
                              onClick={() => handleOpenPdf(video.secure_url)}
                              className="flex items-center justify-center w-12 h-12 transition-all bg-white rounded-full bg-opacity-20 hover:bg-opacity-30"
                            >
                              <FileText className="w-6 h-6 text-white" />
                            </button>
                          </div>
                        </>
                      ) : isImage(video.video_name) ? (
                        // Image Preview
                        <>
                          <div className="absolute inset-0">
                            <img
                              src={video.secure_url}
                              alt={video.title || "Image"}
                              className="object-contain w-full h-full"
                            />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center transition-opacity bg-black opacity-0 bg-opacity-40 hover:opacity-100">
                            <button
                              onClick={() => {
                                setSelectedVideo(video);
                                setShowVideoModal(true);
                              }}
                              className="flex items-center justify-center w-12 h-12 transition-all bg-white rounded-full bg-opacity-20 hover:bg-opacity-30"
                            >
                              <ImageIcon className="w-6 h-6 text-white" />
                            </button>
                          </div>
                        </>
                      ) : (
                        // Video Preview
                        <>
                          <div className="absolute inset-0">
                            <VideoPlayer
                              selectedVideo={{ videoUrl: video.secure_url }}
                              autoPlay={false}
                              controls={false}
                            />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center transition-opacity bg-black opacity-0 bg-opacity-40 hover:opacity-100">
                            <button
                              onClick={() => {
                                setSelectedVideo(video);
                                setShowVideoModal(true);
                              }}
                              className="flex items-center justify-center w-12 h-12 transition-all bg-white rounded-full bg-opacity-20 hover:bg-opacity-30"
                            >
                              <Play className="w-6 h-6 ml-1 text-white" />
                            </button>
                          </div>
                        </>
                      )
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        {isPdf(video.video_name) ? (
                          <FileText className="w-12 h-12 text-red-500" />
                        ) : isImage(video.video_name) ? (
                          <ImageIcon className="w-12 h-12 text-gray-500" />
                        ) : (
                          <Video className="w-12 h-12 text-gray-500" />
                        )}
                      </div>
                    )}

                    {/* Status Badge */}
                    <div className="absolute top-2 right-2">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${video.status?.toLowerCase() === "completed"
                          ? "bg-green-100 text-green-800"
                          : video.status?.toLowerCase() === "processing"
                            ? "bg-blue-100 text-blue-800"
                            : video.status?.toLowerCase() === "failed"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {video.status || "Unknown"}
                      </span>
                    </div>
                  </div>

                  {/* Video Info */}
                  <div className="p-4">
                    <h3
                      className={` font-medium text-lg mb-1 ${"text-white"} truncate whitespace-nowrap max-w-[300px]`}
                      title={video?.title || "Untitled Video"}
                    >
                      {video?.title || "Untitled Video"}
                    </h3>

                    <p
                      className={`text-sm mb-2 truncate ${"text-gray-400"
                        }`}
                    >
                      {video.video_name || "No filename"}
                    </p>
                    <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                      <div className="flex items-center">
                        <Calendar size={12} className="mr-1" />
                        {formatDate(video.created_at)}
                      </div>
                      <div className="flex items-center">
                        {isPdf(video.video_name) ? (
                          <>
                            <FileText size={12} className="mr-1 text-red-500" />
                            <span>PDF</span>
                          </>
                        ) : isImage(video.video_name) ? (
                          <>
                            <ImageIcon size={12} className="mr-1" />
                            <span>Image</span>
                          </>
                        ) : (
                          <>
                            <Play size={12} className="mr-1" />
                            <span>Video</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {video?.csv_id && (
                        <button
                          onClick={() => onViewCsv(video)}
                          className="flex items-center justify-center flex-1 px-3 py-1 text-xs font-medium text-blue-700 transition-colors bg-blue-100 rounded hover:bg-blue-200"
                        >
                          <FileSpreadsheet size={12} className="mr-1" />
                          CSV Data
                        </button>
                      )}
                      <ConfirmDialog
                        title="Delete Video"
                        description="Are you sure you want to delete this video?"
                        onConfirm={() => onDeleteVideo(video.id)}
                        okText="Yes"
                        cancelText="No"
                        destructive
                      >
                        <button className="flex items-center justify-center flex-1 col-start-2 px-3 py-1 text-xs font-medium text-red-700 transition-colors bg-red-100 rounded hover:bg-red-200">
                          <Trash2 size={12} className="mr-1" />
                          Delete
                        </button>
                      </ConfirmDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {videos?.length > 0 && (
          <div className="flex justify-center mt-5 mb-5">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={onPageChange}
              showSizeChanger
              pageSizeOptions={["5", "10", "20", "50"]}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
      </div>
      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden bg-white rounded-lg shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedVideo.video_name || "Video Player"}
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto">
              <div className={sourceTypeFromUrl == "video_analytics" ? "grid grid-cols-1 gap-6 lg:grid-cols-2" : ""}>
                {/* Video Player */}
                <div className="lg:col-span-1">
                  <div className="mb-4 overflow-hidden bg-black rounded-lg aspect-video">
                    {isPdf(selectedVideo.video_name) ? (
                      <div className="flex flex-col items-center justify-center w-full h-full bg-gray-800">
                        <FileText className="w-32 h-32 mb-4 text-red-500" />
                        <button
                          onClick={() =>
                            handleOpenPdf(selectedVideo.secure_url)
                          }
                          className="flex items-center px-4 py-2 text-lg text-white transition-colors bg-red-600 rounded-md hover:bg-red-700"
                        >
                          <FileText size={20} className="mr-2" />
                          Open PDF in New Tab
                        </button>
                      </div>
                    ) : isImage(selectedVideo.video_name) ? (
                      <img
                        src={selectedVideo.secure_url}
                        alt={selectedVideo.title || "Image"}
                        className="object-contain w-full h-full"
                      />
                    ) : (
                      <VideoPlayer
                        selectedVideo={{ videoUrl: selectedVideo.secure_url }}
                        autoPlay={true}
                        controls={true}
                      />
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <div className="px-3 py-1 text-xs text-blue-800 bg-blue-100 rounded-full">
                      {selectedVideo.status || "Unknown"}
                    </div>
                    <div className="px-3 py-1 text-xs text-gray-800 bg-gray-100 rounded-full">
                      {formatDate(selectedVideo.created_at)}
                    </div>
                  </div>
                </div>

                {/* Analysis Content */}
                {sourceTypeFromUrl == "video_analytics" ? (
                  <div className="lg:col-span-1">
                    <h4 className="mb-3 text-lg font-medium text-gray-900">
                      Analysis Results
                    </h4>
                    <div className="p-4 overflow-auto border rounded-lg h-80">
                      {selectedVideo.response ? (
                        <div className="prose max-w-none">
                          {selectedVideo.response
                            .split("\n\n")
                            .map((paragraph, i) => (
                              <div
                                key={i}
                                className="my-4 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: marked(paragraph) }}
                              />
                            ))}
                        </div>
                      ) : (
                        <div className="py-20 text-center text-gray-500">
                          <Video size={40} className="mx-auto mb-4" />
                          <p>No analysis available for this video</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 space-x-3 border-t">
              {selectedVideo?.csv_id && (
                <button
                  onClick={() => {
                    setShowVideoModal(false);
                    onViewCsv(selectedVideo);
                  }}
                  className="px-4 py-2 text-blue-700 transition-colors bg-blue-100 rounded-md hover:bg-blue-200"
                >
                  <FileSpreadsheet size={16} className="inline mr-2" />
                  View CSV Data
                </button>
              )}
              <button
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoDisplayPanel;
